import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  BarChart3, Users, Crown, Tv, DollarSign, Eye, TrendingUp, Clock,
  AlertTriangle, Activity, Download, RefreshCw, Calendar, ChevronDown,
  ArrowUpRight, ArrowDownRight, Minus, Info, Heart, Globe, Monitor,
  Smartphone, Tablet
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { cn } from "@/lib/utils";

const API = "/api";

const PRESETS = [
  { value: "today", label: "Today", labelFr: "Aujourd'hui" },
  { value: "7d", label: "Last 7 Days", labelFr: "7 derniers jours" },
  { value: "30d", label: "Last 30 Days", labelFr: "30 derniers jours" },
  { value: "90d", label: "Last 90 Days", labelFr: "90 derniers jours" },
  { value: "year", label: "This Year", labelFr: "Cette année" },
  { value: "custom", label: "Custom Range", labelFr: "Période personnalisée" },
];

const PIE_COLORS = ["#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#06B6D4", "#F97316", "#EC4899"];

function formatNum(n) {
  if (n == null) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatDuration(secs) {
  if (!secs) return "0s";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function formatMoney(n) {
  if (n == null) return "$0";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Stat Card ───────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, change, tooltip, formatter = formatNum }) {
  const colorMap = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    green: "bg-green-500/10 text-green-400 border-green-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    orange: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    pink: "bg-pink-500/10 text-pink-400 border-pink-500/20",
    indigo: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  };

  return (
    <div className={cn("relative p-4 rounded-xl border transition-all hover:scale-[1.02]", colorMap[color] || colorMap.blue)}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={18} />
        {tooltip && (
          <div className="group relative">
            <Info size={12} className="opacity-40 hover:opacity-80 cursor-help" />
            <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-48 p-2 bg-slate-800 border border-slate-700 rounded-lg text-[10px] text-slate-300 shadow-xl">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-white">{formatter(value)}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{label}</div>
      <div className="mt-1.5">
        {change != null ? (
          <span className={cn("inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md",
            change > 0 ? "bg-green-500/20 text-green-400" : change < 0 ? "bg-red-500/20 text-red-400" : "bg-slate-700/50 text-slate-400")}>
            {change > 0 ? <ArrowUpRight size={10} /> : change < 0 ? <ArrowDownRight size={10} /> : <Minus size={10} />}
            {Math.abs(change)}%
          </span>
        ) : (
          <span className="text-[10px] text-slate-500 italic">No comparison data</span>
        )}
      </div>
    </div>
  );
}

// ── Chart Wrapper ───────────────────────────────────────────────────

function ChartCard({ title, children, isEmpty, emptyMsg = "Not enough data for this period.", loading }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5" data-testid={`chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h3 className="font-bold text-white mb-4 text-sm">{title}</h3>
      {loading ? (
        <div className="h-48 flex items-center justify-center"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : isEmpty ? (
        <div className="h-48 flex items-center justify-center text-sm text-slate-500">{emptyMsg}</div>
      ) : (
        <div className="h-48">{children}</div>
      )}
    </div>
  );
}

// ── Top Table ───────────────────────────────────────────────────────

function TopTable({ title, rows, columns, emptyMsg = "No data available." }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5" data-testid={`table-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h3 className="font-bold text-white mb-4 text-sm">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">{emptyMsg}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                {columns.map(col => (
                  <th key={col.key} className="text-left text-[11px] text-slate-500 font-medium pb-2 pr-3">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/30 last:border-0">
                  {columns.map(col => (
                    <td key={col.key} className="py-2 pr-3 text-slate-300 text-xs">
                      {col.render ? col.render(row, i) : row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [exporting, setExporting] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ preset });
    if (preset === "custom") {
      if (customStart) params.set("start_date", customStart);
      if (customEnd) params.set("end_date", customEnd);
    }
    axios.get(`${API}/admin/analytics?${params}`)
      .then(res => {
        setData(res.data);
        setLastRefresh(new Date());
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || "Failed to load analytics");
        setLoading(false);
      });
  }, [preset, customStart, customEnd]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ preset });
      if (preset === "custom") {
        if (customStart) params.set("start_date", customStart);
        if (customEnd) params.set("end_date", customEnd);
      }
      const res = await axios.get(`${API}/admin/analytics/export?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `djilu_analytics_${preset}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
    setExporting(false);
  };

  const m = data?.metrics || {};
  const charts = data?.charts || {};
  const tables = data?.tables || {};
  const dist = data?.distributions || {};
  const period = data?.period || {};
  const notes = data?.data_notes || {};

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="analytics-loading">
        <div className="flex items-center gap-2"><BarChart3 size={24} className="text-blue-400" /><div className="h-7 w-40 bg-slate-800 rounded animate-pulse" /></div>
        <div className="flex gap-3"><div className="h-10 w-32 bg-slate-800 rounded-xl animate-pulse" /><div className="h-10 w-32 bg-slate-800 rounded-xl animate-pulse" /></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-28 bg-slate-800/50 rounded-xl animate-pulse border border-slate-800/30" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-64 bg-slate-800/50 rounded-xl animate-pulse border border-slate-800/30" />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="space-y-6" data-testid="analytics-error">
        <div className="flex items-center gap-2"><BarChart3 size={24} className="text-blue-400" /><h2 className="text-2xl font-bold text-white">Analytics</h2></div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
          <AlertTriangle size={32} className="mx-auto text-red-400 mb-3" />
          <p className="text-red-400 text-sm mb-4">{error}</p>
          <button onClick={fetchAnalytics} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors" data-testid="analytics-retry">
            <RefreshCw size={14} className="inline mr-1" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={24} className="text-blue-400" />
          <h2 className="text-2xl font-bold text-white">Analytics</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchAnalytics} disabled={loading} className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-50" data-testid="analytics-refresh" title="Refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={handleExport} disabled={exporting} className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white text-xs font-medium transition-colors disabled:opacity-50" data-testid="analytics-export">
            <Download size={13} /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Date Controls */}
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-date-controls">
        {PRESETS.map(p => (
          <button
            key={p.value}
            onClick={() => setPreset(p.value)}
            className={cn("px-3 py-1.5 rounded-lg text-xs font-medium transition-all border",
              preset === p.value
                ? "bg-blue-600 text-white border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.2)]"
                : "bg-slate-800/50 text-slate-400 border-slate-700/50 hover:text-white hover:bg-slate-800")}
            data-testid={`preset-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date range */}
      {preset === "custom" && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="custom-date-range">
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20" data-testid="custom-start" />
          <span className="text-slate-500 text-xs">to</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20" data-testid="custom-end" />
        </div>
      )}

      {/* Period info bar */}
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
        <span><Calendar size={11} className="inline mr-1" />{period.start?.slice(0, 10)} → {period.end?.slice(0, 10)}</span>
        <span>vs {period.comparison_start?.slice(0, 10)} → {period.comparison_end?.slice(0, 10)}</span>
        {lastRefresh && <span>Last refreshed: {lastRefresh.toLocaleTimeString()}</span>}
        {!notes.tracking_active && (
          <span className="text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded">
            Event tracking just started — historical data uses channel view counts
          </span>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Views" value={m.total_views?.value} icon={Eye} color="blue" change={m.total_views?.change}
          tooltip="Valid playback starts from event tracking, or channel view counts before tracking was active." />
        <StatCard label="Unique Viewers" value={m.unique_viewers?.value} icon={Users} color="purple" change={m.unique_viewers?.change}
          tooltip="Distinct authenticated users or anonymous sessions with playback events." />
        <StatCard label="Live Viewers" value={m.live_viewers?.value} icon={Activity} color="red" change={m.live_viewers?.change}
          tooltip="Active sessions seen in the last 90 seconds." />
        <StatCard label="New Users" value={m.new_users?.value} icon={Users} color="green" change={m.new_users?.change}
          tooltip="Users who registered during this period." />
        <StatCard label="Premium Subscribers" value={m.premium_subscribers?.value} icon={Crown} color="amber" change={m.premium_subscribers?.change}
          tooltip="Users with an active premium membership." />
        <StatCard label="Conversion Rate" value={m.subscription_conversion?.value} icon={TrendingUp} color="indigo" change={m.subscription_conversion?.change}
          tooltip="Percentage of total users who are premium." formatter={v => v != null ? `${v}%` : "—"} />
        <StatCard label="Total Revenue" value={m.total_revenue?.value} icon={DollarSign} color="emerald" change={m.total_revenue?.change}
          tooltip="Sum of completed payments in the selected period." formatter={formatMoney} />
        <StatCard label="Avg Watch Time" value={m.avg_watch_time?.value} icon={Clock} color="cyan" change={m.avg_watch_time?.change}
          tooltip="Average playback duration from completed viewing sessions." formatter={formatDuration} />
        <StatCard label="Active Channels" value={m.active_channels?.value} icon={Tv} color="orange" change={m.active_channels?.change}
          tooltip="Total channels currently active and available for streaming." />
        <StatCard label="Playback Errors" value={m.failed_streams?.value} icon={AlertTriangle} color="red" change={m.failed_streams?.change}
          tooltip="Failed stream playback attempts reported by viewers." />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Over Time */}
        <ChartCard title="Views Over Time" isEmpty={!charts.views_over_time?.length} loading={loading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.views_over_time}>
              <defs><linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="views" stroke="#3B82F6" fill="url(#viewsGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* New Users */}
        <ChartCard title="New User Registrations" isEmpty={!charts.new_users_by_day?.length} loading={loading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.new_users_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="users" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue */}
        <ChartCard title="Revenue" isEmpty={!charts.revenue_by_day?.length} loading={loading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.revenue_by_day}>
              <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} formatter={v => [`$${v}`, "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Device Distribution */}
        <ChartCard title="Device Distribution" isEmpty={!dist.device?.length} loading={loading}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={dist.device} dataKey="count" nameKey="device" cx="50%" cy="50%" outerRadius={65} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {(dist.device || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top-performing Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopTable title="Top Channels"
          rows={tables.top_channels || []}
          columns={[
            { key: "rank", label: "#", render: (_, i) => <span className="text-slate-500 font-mono">{i + 1}</span> },
            { key: "name", label: "Channel", render: (r) => (
              <div className="flex items-center gap-2">
                {r.logo_url && <img src={r.logo_url} alt="" className="w-5 h-5 rounded object-cover" onError={e => { e.target.style.display = "none"; }} />}
                <span className="text-white font-medium truncate max-w-[140px]">{r.name}</span>
              </div>
            )},
            { key: "views", label: "Views", render: (r) => formatNum(r.views) },
            { key: "category_name", label: "Category" },
          ]}
        />

        <TopTable title="Top Categories"
          rows={tables.top_categories || []}
          columns={[
            { key: "rank", label: "#", render: (_, i) => <span className="text-slate-500 font-mono">{i + 1}</span> },
            { key: "name", label: "Category", render: (r) => <span className="text-white font-medium">{r.name_fr || r.name}</span> },
            { key: "channel_count", label: "Channels", render: (r) => formatNum(r.channel_count) },
          ]}
        />

        <TopTable title="Top Countries"
          rows={tables.top_countries || []}
          columns={[
            { key: "rank", label: "#", render: (_, i) => <span className="text-slate-500 font-mono">{i + 1}</span> },
            { key: "name", label: "Country", render: (r) => <span className="text-white font-medium">{r.name}</span> },
            { key: "code", label: "Code" },
            { key: "channel_count", label: "Channels", render: (r) => formatNum(r.channel_count) },
          ]}
        />

        <TopTable title="Most Favorited Channels"
          rows={tables.most_favorited || []}
          columns={[
            { key: "rank", label: "#", render: (_, i) => <span className="text-slate-500 font-mono">{i + 1}</span> },
            { key: "name", label: "Channel", render: (r) => (
              <div className="flex items-center gap-2">
                {r.logo_url && <img src={r.logo_url} alt="" className="w-5 h-5 rounded object-cover" onError={e => { e.target.style.display = "none"; }} />}
                <span className="text-white font-medium truncate max-w-[140px]">{r.name}</span>
              </div>
            )},
            { key: "favorite_count", label: "Favorites", render: (r) => <span className="flex items-center gap-1"><Heart size={11} className="text-pink-400" />{r.favorite_count}</span> },
          ]}
        />

        {(tables.error_channels || []).length > 0 && (
          <TopTable title="Channels with Playback Errors"
            rows={tables.error_channels}
            columns={[
              { key: "rank", label: "#", render: (_, i) => <span className="text-slate-500 font-mono">{i + 1}</span> },
              { key: "name", label: "Channel", render: (r) => <span className="text-white font-medium">{r.name}</span> },
              { key: "error_count", label: "Errors", render: (r) => <span className="text-red-400 font-mono">{r.error_count}</span> },
            ]}
          />
        )}
      </div>

      {/* Language Distribution */}
      {(dist.language || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Language Distribution" isEmpty={false}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dist.language} dataKey="count" nameKey="language" cx="50%" cy="50%" outerRadius={65} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {(dist.language || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Data integrity note */}
      <div className="text-[10px] text-slate-600 border-t border-slate-800/50 pt-3 mt-3">
        <p>Data sources: Revenue from payment records • Users from registration records • Views from {notes.views_source === "analytics_events" ? "event tracking" : "channel view counters (backfill)"} • Tracking active since: {notes.tracking_active ? "now" : "pending first events"}</p>
      </div>
    </div>
  );
}
